import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { CabinetTemplate, CreateCabinetTemplate, UpdateCabinetTemplate, TemplateHardwareType, CreateTemplateHardware } from '@estimator/shared';

export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data } = await apiClient.get<CabinetTemplate[]>('/cabinet-templates');
      return data;
    },
  });
}

export function useProduct(id: string | null) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: async () => {
      const { data } = await apiClient.get<CabinetTemplate>(`/cabinet-templates/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCabinetTemplate) => {
      const { data } = await apiClient.post<CabinetTemplate>('/cabinet-templates', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateCabinetTemplate & { id: string }) => {
      const { data } = await apiClient.patch<CabinetTemplate>(`/cabinet-templates/${id}`, body);
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products', vars.id] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/cabinet-templates/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useAddProductHardware() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, ...body }: CreateTemplateHardware & { templateId: string }) => {
      const { data } = await apiClient.post<TemplateHardwareType>(`/cabinet-templates/${templateId}/hardware`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useRemoveProductHardware() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, hwId }: { templateId: string; hwId: string }) => {
      await apiClient.delete(`/cabinet-templates/${templateId}/hardware/${hwId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useUpdateProductHardwareQuantity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, hwId, quantity }: { templateId: string; hwId: string; quantity: number }) => {
      const { data } = await apiClient.patch<TemplateHardwareType>(`/cabinet-templates/${templateId}/hardware/${hwId}`, {
        quantity_per_cabinet: quantity,
      });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
  });
}
